import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { getRoles } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { History, Plus } from "lucide-react";
import { RoleNameDialog } from "@/components/roles/RoleNameDialog";
import { AccessCheckNote } from "@/components/roles/AccessCheckNote";
import { RoleRow } from "@/components/roles/RoleRow";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

/* ROLES FROM A SCREEN (Batch 3): make, rename, retire (never delete), restore — all logged, none changes access. */
export function RolesPanel({ onChanged }) {
  const [data, setData] = useState(null);
  const [naming, setNaming] = useState(null);   // {role} for rename, {} for new
  const [busy, setBusy] = useState("");
  const [retiring, setRetiring] = useState(null);

  const load = () => api.get("/admin/roles/manage").then(({ data }) => setData(data))
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const run = async (key, call, ok) => {
    setBusy(key);
    try {
      await call();
      toast.success(ok);
      setNaming(null);
      await Promise.all([load(), getRoles(true)]);
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const save = (name) => (naming.role
    ? run(naming.role.key, () => api.put(`/admin/roles/${encodeURIComponent(naming.role.key)}/name`, { name }), `Renamed to “${name.trim()}” — nobody's access changed`)
    : run("new", () => api.post("/admin/roles", { name }), `“${name.trim()}” made — every toggle off, nobody in it`));
  const act = (r, verb) => run(r.key, () => api.post(`/admin/roles/${encodeURIComponent(r.key)}/${verb}`),
    verb === "retire" ? `“${r.name}” retired — its toggles are kept` : `“${r.name}” restored, toggles as they were`);

  if (!data) return <div className="font-mono text-xs text-slate-500 mb-4">LOADING ROLES…</div>;
  return (
    <section data-testid="roles-panel" className="mb-6 border border-slate-200 bg-white rounded-lg p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-display text-lg font-bold mr-auto">Roles</h2>
        <Link to="/permissions/history" data-testid="role-history-link"
          className="inline-flex items-center gap-1 text-sm font-semibold text-[#1F1F1F] underline underline-offset-2">
          <History className="w-4 h-4" /> Role change history
        </Link>
        <Button data-testid="role-new-button" size="sm" onClick={() => setNaming({})} className="bg-[#E0781A] hover:bg-[#C76714] text-white">
          <Plus className="w-4 h-4 mr-1" /> New role
        </Button>
      </div>
      <AccessCheckNote check={data.access_check} />
      <div className="divide-y divide-slate-100">
        {data.roles.map((r) => <RoleRow key={r.key} r={r} busy={busy === r.key} onRename={() => setNaming({ role: r })}
          onAct={(role, verb) => (verb === "retire" ? setRetiring(role) : act(role, verb))} />)}
      </div>
      <p className="text-xs text-slate-500">
        A role is retired, never deleted, and only once nobody holds it. <Badge className="rounded bg-slate-100 text-slate-600 text-[10px] hover:bg-slate-100">Built in</Badge> roles came with the app; they can be renamed and retired like any other. Admin can't.
      </p>
      <AlertDialog open={!!retiring} onOpenChange={(o) => { if (!o) setRetiring(null); }}>
        <AlertDialogContent data-testid="role-retire-dialog" className="rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Retire “{retiring?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Nobody holds it, so nobody's access changes. It stops appearing in the role pickers and the grid;
              its toggles are kept exactly and Restore brings it back as it was. It is never deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="role-retire-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="role-retire-confirm" className="bg-[#1F1F1F] hover:bg-black"
              onClick={() => { const r = retiring; setRetiring(null); act(r, "retire"); }}>Retire</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <RoleNameDialog open={!!naming} role={naming?.role} busy={!!busy} onClose={() => setNaming(null)} onSave={save} />
    </section>
  );
}
